import { Grid, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TestIdIdentifier } from '../../../util/identifiers/identifiers.util';

// Currently negative numbers are not allowed anywhere, could change in future.
const invalidNumberCharacters = new Set(['e', 'E', '+', '-']);

interface TextInputProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  testId?: TestIdIdentifier;
  type?: React.HTMLInputTypeAttribute;
  label?: string;
  mandatory?: boolean;
  dynamicUpdate?: boolean;
  width?: string;
  autoComplete?: string;
  draggable?: boolean;
  onDragStart?: (event: any) => void;
  min?: number;
  max?: number;
  tooltip?: string;
  heightClass?: string;
  suffix?: string;
  autoFocus?: boolean;
  id?: string;
}

export default function TextInput({
  value,
  onChange,
  placeholder = '',
  disabled,
  testId,
  type,
  label,
  mandatory = false,
  dynamicUpdate = false,
  width,
  autoComplete,
  draggable = false,
  min,
  max,
  tooltip,
  onDragStart,
  heightClass = 'h-10',
  suffix,
  autoFocus = false,
  id,
}: TextInputProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string>(value || '');

  const [focus, setFocus] = useState<boolean>(false);

  const setChange = (value: string) => {
    setText(value);
    onChange(value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.currentTarget.value;

    if (type === 'number') {
      const parsed = parseFloat(value);

      if (value !== '' && Number.isNaN(parsed)) return;

      if (min !== undefined && parsed < min) {
        return setChange(min.toString());
      }

      if (max !== undefined && parsed > max) {
        return setChange(max.toString());
      }
    }

    setChange(value);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (dynamicUpdate) setText(value || '');
  }, [value]);

  return (
    <Grid container width={width || 'auto'} marginX={'auto'}>
      <Grid item display='flex' marginY={'auto'}>
        {label ? <p className='mb-1 text-sm font-medium text-slate-800'>{label}</p> : null}
        {mandatory ? <p className='ml-1 text-sm font-medium text-red-500'>*</p> : null}
      </Grid>
      {tooltip ? (
        <Grid ml={0.5} className='hover:cursor-pointer' item display='flex' marginY={'auto'}>
          <Tooltip open={open} onClose={handleClose} onOpen={handleOpen} title={tooltip}>
            <InfoOutlinedIcon sx={{ width: '25px' }} />
          </Tooltip>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        {suffix ? ( // For auto complete reasons
          <Grid
            container
            className={`${
              disabled ? 'bg-gray-100 text-gray-400' : 'bg-white'
            } border ${heightClass} my-auto inline-block   w-full rounded border border-slate-300 placeholder-slate-400 shadow-sm sm:text-sm ${
              focus ? 'border-sky-500 ring-1 ring-sky-500' : ''
            }`}
            justifyContent={'flex-end'}
          >
            <Grid item xs={suffix ? 8 : 12} marginY={'auto'}>
              <input
                id={id}
                autoFocus={autoFocus}
                type={type}
                data-testid={testId?.name}
                draggable={draggable}
                onDragStart={onDragStart}
                autoComplete={autoComplete}
                min={min}
                value={text}
                disabled={disabled}
                onChange={handleChange}
                onKeyDown={event => {
                  if (type === 'number' && invalidNumberCharacters.has(event.key.toLowerCase())) event.preventDefault();
                }}
                placeholder={placeholder}
                className={`h-full w-full rounded pl-3 placeholder-slate-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400 sm:text-sm`}
                onFocus={event => setFocus(true)}
                onBlur={event => setFocus(false)}
              />
            </Grid>
            <Grid item xs={4} my={'auto'} textAlign={'end'}>
              <p className='select-none pr-2 text-gray-400'>{suffix}</p>
            </Grid>
          </Grid>
        ) : (
          <Grid container className={`${heightClass}`} my={'auto'}>
            <input
              data-testid={testId?.name}
              draggable={draggable}
              onDragStart={onDragStart}
              autoComplete={autoComplete}
              type={type}
              min={min}
              value={text}
              onKeyDown={event => {
                if (type === 'number' && invalidNumberCharacters.has(event.key.toLowerCase())) event.preventDefault();
              }}
              disabled={disabled}
              onChange={handleChange}
              className={`block h-full w-full rounded border border-slate-300 bg-white px-3 py-2 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100 disabled:text-gray-400 sm:text-sm`}
              placeholder={placeholder}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
